import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopupsPage from "../../entities/Topups";
import { topupService } from "../../api";
import AuthService from "../../api/AuthService";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import { useAlert } from "../../context/AlertContext";
import { TOPUP_STATUSES } from "../../constants/planConstants";
import { redirectToCheckout } from "../../utils/checkout";
import {
  classifyCheckoutError,
  readTopupReturn,
  stripTopupReturnParams,
  TOPUP_PENDING_PURCHASE_KEY,
} from "../../utils/topupCheckout";

// The only allowance with an existing customer-side count surface. Everything
// else is reported as a limit without a numerator rather than inventing one.
export const NUMERATOR_ALLOWANCE = "activeEmployees";

// Frontend policy, not a backend guarantee: the guide asks for a single retry
// when the purchase is still settling. Never a loop.
export const CONFIRM_RETRY_DELAY_MS = 2500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  return [];
};

const Topups = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState("");
  const [usageLoading, setUsageLoading] = useState(false);

  const [available, setAvailable] = useState([]);
  const [availableError, setAvailableError] = useState("");
  const [availableLoading, setAvailableLoading] = useState(false);

  const [purchases, setPurchases] = useState([]);
  const [purchasesError, setPurchasesError] = useState("");
  const [purchasesLoading, setPurchasesLoading] = useState(false);

  // Current-usage numerator, sourced from the same call the customer dashboard
  // already makes. Never derived from topup purchase history.
  const [employeeCount, setEmployeeCount] = useState(null);

  // Purchase flow
  const [selectedPack, setSelectedPack] = useState(null);
  const [quote, setQuote] = useState(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  // Reconciliation after the payment return
  const [reconciling, setReconciling] = useState(false);
  const [reconciledPurchase, setReconciledPurchase] = useState(null);
  const [reconcileNotice, setReconcileNotice] = useState("");
  const returnHandled = useRef(false);

  useEffect(() => {
    fetchUsage();
    fetchAvailable();
    fetchPurchases();
    fetchEmployeeCount();
  }, []);

  // Payment return. The backend appends purchaseId/reference/status; `reference`
  // is informational and `status` is advisory — neither is trusted, and the
  // authoritative state always comes from the backend.
  //
  // Precedence per the agreed contract: the purchaseId persisted by this
  // checkout flow wins, and the URL parameter is only a fallback for when no
  // stored id exists (e.g. storage was unavailable at redirect time).
  useEffect(() => {
    if (returnHandled.current) return;

    const { purchaseId } = readTopupReturn(location.search);
    const storedPurchaseId = localStorage.getItem(TOPUP_PENDING_PURCHASE_KEY) || "";
    const target = storedPurchaseId || purchaseId;
    if (!target) return;

    returnHandled.current = true;
    stripReturnParams();
    reconcilePurchase(target);
    // eslint-disable-next-line
  }, [location.search]);

  const stripReturnParams = () => {
    const nextSearch = stripTopupReturnParams(window.location.search);
    const url = `${window.location.pathname}${nextSearch}${window.location.hash || ""}`;
    window.history.replaceState({}, "", url);
  };

  const fetchUsage = async () => {
    setUsageLoading(true);
    setUsageError("");
    LoaderHelper.loaderStatus(true);
    try {
      const result = await topupService.usage();
      if (result?.success === false) {
        setUsage(null);
        setUsageError(result?.message || "Could not load your plan limits.");
        showAlert(result?.message || "Could not load your plan limits.", "error");
        return;
      }
      const payload = result?.data && !Array.isArray(result.data) ? result.data : result;
      setUsage({
        planName: payload?.planName || "",
        allowances:
          payload?.allowances && typeof payload.allowances === "object"
            ? payload.allowances
            : {},
      });
    } catch (error) {
      setUsage(null);
      setUsageError(error?.message || "Error loading your plan limits");
      showAlert(error?.message || "Error loading your plan limits", "error");
    } finally {
      setUsageLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const fetchAvailable = async () => {
    setAvailableLoading(true);
    setAvailableError("");
    try {
      const result = await topupService.available();
      if (result?.success === false) {
        setAvailable([]);
        setAvailableError(result?.message || "Could not load available add-ons.");
        return;
      }
      setAvailable(normalizeListResponse(result));
    } catch (error) {
      setAvailable([]);
      setAvailableError(error?.message || "Error loading available add-ons");
    } finally {
      setAvailableLoading(false);
    }
  };

  const fetchPurchases = async () => {
    setPurchasesLoading(true);
    setPurchasesError("");
    try {
      const result = await topupService.purchases();
      if (result?.success === false) {
        setPurchases([]);
        setPurchasesError(result?.message || "Could not load your topups.");
        return;
      }
      setPurchases(normalizeListResponse(result));
    } catch (error) {
      setPurchases([]);
      setPurchasesError(error?.message || "Error loading your topups");
    } finally {
      setPurchasesLoading(false);
    }
  };

  const fetchEmployeeCount = async () => {
    try {
      const result = await AuthService.getPayrollStats();
      const stats =
        result?.data?.payrollStats || result?.payrollStats || result?.data || result;
      const total = stats?.totalEmployees;
      setEmployeeCount(
        total === null || total === undefined ? null : Number(total)
      );
    } catch (error) {
      // The numerator is a nice-to-have; its absence must not break the limits
      // view, which is the authoritative part of this screen.
      setEmployeeCount(null);
    }
  };

  /* ----------------------------- purchase ----------------------------- */

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setQuote(null);
    setCheckoutError("");
  };

  const handleClosePurchase = () => {
    setSelectedPack(null);
    setQuote(null);
    setCheckoutError("");
  };

  // Opens a checkout session. The response is the only authoritative source of
  // amount/currency/expiresAt — nothing here multiplies price by quantity.
  const handleStartCheckout = async (pack, quantity) => {
    const packId = pack?._id || pack?.id;
    if (!packId) return;

    setCheckoutSubmitting(true);
    setCheckoutError("");
    try {
      const result = await topupService.checkout(packId, quantity);

      if (result?.success === false || !result?.data) {
        await handleCheckoutFailure(result);
        return;
      }

      const data = result.data;
      if (!data?.authorizationUrl || !data?.purchaseId) {
        setCheckoutError(
          "Checkout could not be started. Please try again."
        );
        return;
      }
      setQuote(data);
    } catch (error) {
      setCheckoutError(error?.message || "Could not start checkout.");
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const handleCheckoutFailure = async (result) => {
    const { kind, message } = classifyCheckoutError(result);

    switch (kind) {
      case "forbidden":
        setCheckoutError(message);
        return;
      case "no_subscription":
        showAlert(message, "info");
        handleClosePurchase();
        navigate("/planManagement");
        return;
      case "billing_incomplete":
        showAlert(message, "info");
        handleClosePurchase();
        navigate("/dashboard/profile");
        return;
      case "outstanding_balance":
        // The existing outstanding-balance banner owns the renewal journey.
        showAlert(message, "info");
        handleClosePurchase();
        navigate("/dashboard/profile");
        return;
      case "pack_gone":
      case "no_country_pricing":
      case "not_on_plan":
        setCheckoutError(message);
        await fetchAvailable();
        return;
      case "quantity_out_of_range":
        setCheckoutError(message);
        return;
      case "no_billing_date":
        setCheckoutError(
          message ||
            "Your billing cycle information is unavailable, so payment cannot be started. Please contact support."
        );
        return;
      case "server_error":
        // A purchase row may already have been created and marked failed, so
        // never claim nothing happened — reconcile from the history instead.
        setCheckoutError(
          "We could not confirm whether the purchase was started. Your topup history has been refreshed."
        );
        await fetchPurchases();
        return;
      default:
        setCheckoutError(message || "Could not start checkout.");
    }
  };

  // Store the purchaseId before leaving the app so reconciliation can run even
  // if the return URL arrives without params. This is a pointer only — never
  // treated as payment state.
  const handleProceedToPayment = () => {
    if (!quote?.authorizationUrl) return;
    try {
      localStorage.setItem(TOPUP_PENDING_PURCHASE_KEY, String(quote.purchaseId));
    } catch (e) {
      /* storage unavailable — reconciliation falls back to the URL params */
    }
    redirectToCheckout(quote.authorizationUrl);
  };

  /* --------------------------- reconciliation -------------------------- */

  // Only a real purchase record counts. Guarded against an HTTP status field
  // (e.g. 502) being mistaken for a purchase lifecycle status.
  const readPurchase = (result) => {
    if (!result) return null;
    const candidate =
      result?.data && !Array.isArray(result.data) ? result.data : result;
    return TOPUP_STATUSES.includes(candidate?.status) ? candidate : null;
  };

  // Confirm is reconciliation, not activation — the webhook may already have
  // activated the purchase. Exactly one retry while the payment is settling.
  const reconcilePurchase = async (purchaseId) => {
    setReconciling(true);
    setReconcileNotice("");
    try {
      let result = await topupService.confirm(purchaseId);
      let purchase = readPurchase(result);

      const stillPending =
        purchase?.status === "pending" ||
        // 502: live re-verification failed and the purchase stays pending.
        (result?.success === false && !purchase);

      if (stillPending) {
        await delay(CONFIRM_RETRY_DELAY_MS);
        result = await topupService.confirm(purchaseId);
        purchase = readPurchase(result) || purchase;
      }

      localStorage.removeItem(TOPUP_PENDING_PURCHASE_KEY);
      setReconciledPurchase(purchase || null);

      if (!purchase) {
        setReconcileNotice(
          "Your payment is still being processed. Refresh in a moment to see the latest status."
        );
      }

      // Authoritative state always comes from a re-fetch, never from local
      // mutation of usage or purchases.
      await Promise.all([fetchUsage(), fetchPurchases()]);
    } catch (error) {
      localStorage.removeItem(TOPUP_PENDING_PURCHASE_KEY);
      setReconciledPurchase(null);
      setReconcileNotice(
        "Your payment is still being processed. Refresh in a moment to see the latest status."
      );
      await Promise.all([fetchUsage(), fetchPurchases()]);
    } finally {
      setReconciling(false);
    }
  };

  const handleRefreshStatus = async () => {
    setReconcileNotice("");
    await Promise.all([fetchUsage(), fetchPurchases()]);
  };

  const handleDismissOutcome = () => {
    setReconciledPurchase(null);
    setReconcileNotice("");
  };


  return (
    <TopupsPage
      usage={usage}
      usageLoading={usageLoading}
      usageError={usageError}
      available={available}
      availableLoading={availableLoading}
      availableError={availableError}
      purchases={purchases}
      purchasesLoading={purchasesLoading}
      purchasesError={purchasesError}
      employeeCount={employeeCount}
      numeratorAllowance={NUMERATOR_ALLOWANCE}
      selectedPack={selectedPack}
      quote={quote}
      checkoutSubmitting={checkoutSubmitting}
      checkoutError={checkoutError}
      reconciling={reconciling}
      reconciledPurchase={reconciledPurchase}
      reconcileNotice={reconcileNotice}
      onSelectPack={handleSelectPack}
      onClosePurchase={handleClosePurchase}
      onStartCheckout={handleStartCheckout}
      onProceedToPayment={handleProceedToPayment}
      onRefreshStatus={handleRefreshStatus}
      onDismissOutcome={handleDismissOutcome}
      onBack={() => navigate("/dashboard/profile")}
    />
  );
};

export default Topups;
